import { GetServerSideProps } from "next";
import {
  useLoadScript,
  GoogleMap,
  MarkerF,
  MarkerClustererF,
  OverlayViewF,
  OverlayView,
} from "@react-google-maps/api";
import { Text } from "~/components/Text";
import { handleRequestError } from "~/utils/rollbar";
import { findStoresWithLocation } from "~/strapi/api";
import { StrapiStore } from "~/shared-types";
import { StoreCard } from "~/components/StoreCard";
import { useRouter } from "next/router";
import { getRoute } from "~/utils/getRoute";
import { useLayout } from "~/layouts/layoutContext";
import { Inter } from "next/font/google";
import mapsStyle from "./mapsStyle.json";
import { Fragment, useState } from "react";

const inter = Inter({ subsets: ["latin"] });
const DENMARK = { lat: 56.2639, lng: 9.5018 };

type ServerSideProps = {
  stores: StrapiStore[];
  queryStore: StrapiStore | null;
};

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (
  context
) => {
  const stores = await findStoresWithLocation().catch(
    handleRequestError(context.req, [])
  );

  const queryStore = stores.find(
    (store) => store.id === Number(context.query.b)
  );

  return { props: { stores, queryStore: queryStore ?? null } };
};

const ExplorePage = ({ stores, queryStore }: ServerSideProps) => {
  const [focusedStore, setFocusedStore] = useState(queryStore);
  const [position, setPosition] = useState<google.maps.LatLngLiteral>(
    queryStore?.attributes.location ?? DENMARK
  );
  const [zoom, setZoom] = useState(queryStore ? 15 : 8);

  const { setIsFullScreen } = useLayout();
  setIsFullScreen(true);

  const router = useRouter();
  const onMarkerClick = (store?: StrapiStore) => {
    if (!store) {
      router.replace(getRoute("/explore"));
      setFocusedStore(null);

      return;
    }

    router.replace(getRoute("/explore", null, { query: { b: store.id } }));
    setFocusedStore(store);
    // We know the location is there, because we only show the marker if it is.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setPosition(store.attributes.location!);
    setZoom(15);
  };

  const { isLoaded } = useLoadScript({
    id: "google-maps-script",
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
  });

  if (!isLoaded) {
    return <Text>Henter kort...</Text>;
  }

  const bigSize = new window.google.maps.Size(35, 41);
  const smallSize = new window.google.maps.Size(22, 26);

  return (
    <GoogleMap
      mapContainerStyle={{
        width: "100%",
        //  The subtracted height is based on the NavBar height.
        height: "calc(100vh - var(--navbar-height))",
      }}
      zoom={zoom}
      center={position}
      options={{
        styles: mapsStyle,
      }}
    >
      {/* NB: Markers do not show up when React is in StrictMode */}
      <MarkerClustererF
        options={{
          styles: [
            {
              url: "/marker-cluster.svg",
              width: 60,
              height: 60,
              fontFamily: inter.style.fontFamily,
              textColor: "white",
            },
          ],
        }}
      >
        {(clusterer) => (
          <>
            {stores.map((store) =>
              store?.attributes?.location ? (
                <Fragment key={store.id}>
                  <OverlayViewF
                    position={position}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    {focusedStore && (
                      <StoreCard
                        closable
                        onClose={() => onMarkerClick()}
                        className="translate-x-[-50%] translate-y-[calc(-100%-41px-5px)] mt-0 w-full"
                        store={focusedStore}
                      />
                    )}
                  </OverlayViewF>
                  <MarkerF
                    position={store.attributes.location}
                    clusterer={clusterer}
                    options={{
                      icon: {
                        url: "/marker.png",
                        scaledSize:
                          store.id === focusedStore?.id ? bigSize : smallSize,
                      },
                      opacity:
                        !focusedStore || store.id === focusedStore.id ? 1 : 0.5,
                    }}
                    onClick={() => onMarkerClick(store)}
                  />
                </Fragment>
              ) : null
            )}
          </>
        )}
      </MarkerClustererF>
    </GoogleMap>
  );
};

export default ExplorePage;
